import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { addressLike } from "../address";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";

// Asking people to review recordings, by email (byj-1wv.44): addresses typed
// — there is no directory of people — and a note. The relay mails each one
// once, naming every recording; a person it knows is made a reviewer at once.

export const maxReviewers = 10;
const noteMax = 1000;

/** The addresses a field holds: split on commas, semicolons, spaces and lines, each once. */
export function parseEmails(text: string): { good: string[]; bad: string[] } {
  const seen = new Set<string>();
  const good: string[] = [];
  const bad: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const e = raw.trim();
    if (!e || seen.has(e.toLowerCase())) continue;
    seen.add(e.toLowerCase());
    (addressLike.test(e) ? good : bad).push(e);
  }
  return { good, bad };
}

type Asking = { n: number; resolve: (req: { emails: string[]; note: string } | null) => void };

/** `[ask, dialog]`: `ask(n)` resolves to the addresses and note, or null when cancelled. */
export function useRequestReview(): [(n: number) => Promise<{ emails: string[]; note: string } | null>, ReactNode] {
  const [asking, setAsking] = useState<Asking | null>(null);
  const ask = useCallback((n: number) => new Promise<{ emails: string[]; note: string } | null>((resolve) => setAsking({ n, resolve })), []);
  const done = useCallback(
    (req: { emails: string[]; note: string } | null) => {
      asking?.resolve(req);
      setAsking(null);
    },
    [asking],
  );
  return [ask, asking ? <RequestReviewDialog n={asking.n} onDone={done} /> : null];
}

function RequestReviewDialog({ n, onDone }: { n: number; onDone: (req: { emails: string[]; note: string } | null) => void }) {
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  const { good, bad } = parseEmails(emails);
  const tooMany = good.length > maxReviewers;
  const ready = good.length > 0 && bad.length === 0 && !tooMany;

  // Esc closes it alone, and Tab stays in it (components/Modal)
  const keys = useModalKeys(() => onDone(null));
  useEffect(() => {
    field.current?.focus();
  }, []);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onDone(null)}>
      <form
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-review-heading"
        onKeyDown={keys}
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onDone({ emails: good, note: note.trim() });
        }}
      >
        <h3 id="request-review-heading">{t("recordings:requestReviewHeading", { count: n })}</h3>
        <label className="field">
          {t("recordings:reviewEmailsLabel")}
          <textarea ref={field} rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} />
        </label>
        <p className="muted small">{t("recordings:reviewEmailsHint")}</p>
        {bad.length > 0 && <p className="error small">{t("recordings:reviewBadEmails", { list: bad.join(", ") })}</p>}
        {tooMany && <p className="error small">{t("recordings:reviewTooMany")}</p>}
        <label className="field">
          {t("recordings:reviewNoteLabel")}
          <textarea rows={3} maxLength={noteMax} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="row modal-actions">
          <button type="button" onClick={() => onDone(null)}>
            {t("common:cancel")}
          </button>
          <button type="submit" className="primary" disabled={!ready}>
            {t("common:send")}
          </button>
        </div>
      </form>
    </div>
  );
}
